import type { ExportConfig } from '../utils/exportSelection';
// src/hooks/useApi.ts - CRITICAL FIX APPLIED: Search functionality now works correctly.
import { useCallback, useEffect, useState, useRef } from 'react';
import { useAppContext } from './useAppContext';
import { useAuth } from './useAuth';
import type {
  SearchResponse,
  MetadataResponse,
  SchemaResponse,
  SearchState,
  RequestCache,
  DataSourceType,
  // UserList,
  AdminAnalyticsSummaryResponse,
  UsersResponse,
  User
} from '../types';
import { API_BASE_URL, CACHE_DURATION } from '../constants';
import { BackoffManager, retryAfterMs } from '../utils/rateLimits';
import { readApiResponse } from '../utils/apiResponse';
import { requestTracker, type RequestTrackerState, type RequestType } from '../utils/requestTracker';

// Only cache people data
const peopleRequestCache: RequestCache = {};

interface RateLimitInfo {
  remaining: number;
  reset: number;
  limit: number;
}

class CircuitBreaker {
  private failureCount = 0;
  private lastFailureTime = 0;
  private state: 'closed' | 'open' | 'half-open' = 'closed';
  private maxFailures: number;
  private resetTimeoutMs: number;
  
  constructor(maxFailures = 5, resetTimeoutMs = 30000) {
    this.maxFailures = maxFailures;
    this.resetTimeoutMs = resetTimeoutMs;
  }
  
  canMakeRequest(): boolean {
    const now = Date.now();
    if (this.state === 'open') {
      if (now - this.lastFailureTime > this.resetTimeoutMs) {
        this.state = 'half-open';
        return true;
      }
      return false;
    }
    return true;
  }
  
  onSuccess() {
    this.failureCount = 0;
    this.state = 'closed';
  }
  
  onFailure() {
    this.failureCount++;
    this.lastFailureTime = Date.now();
    if (this.failureCount >= this.maxFailures) {
      this.state = 'open';
    }
  }
  
  getState() {
    return {
      state: this.state,
      failureCount: this.failureCount,
      canRequest: this.canMakeRequest()
    };
  }
}

const circuitBreaker = new CircuitBreaker();
const backoffManager = new BackoffManager();

interface AuthError extends Error {
  code?: string;
  status: number;
  isAuthError: boolean;
  isRateLimit: boolean;
  isPermissionError: boolean;
  retryMs?: number | null;
}

const createAuthError = (status: number, message: string): AuthError => {
  const error = new Error(message) as AuthError;
  error.status = status;
  error.isAuthError = status === 401;
  error.isRateLimit = status === 429;
  error.isPermissionError = status === 403;
  return error;
};

const getAuthHeaders = (apiKey: string | null, isFormData: boolean = false): Record<string, string> => {
  if (!apiKey || apiKey === 'null' || apiKey === 'undefined') {
    throw new Error('No valid API key available for authentication');
  }
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${apiKey}`
  };
  
  // Only set Content-Type for JSON requests, not FormData
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  
  return headers;
};

const parseRateLimitHeaders = (headers: Headers): RateLimitInfo | null => {
  try {
    const remaining = headers.get('X-RateLimit-Remaining-Minute');
    const reset = headers.get('X-RateLimit-Reset-Minute');
    const limit = headers.get('X-RateLimit-Limit-Minute');
    if (remaining && reset && limit) {
      return {
        remaining: parseInt(remaining, 10),
        reset: parseInt(reset, 10),
        limit: parseInt(limit, 10)
      };
    }
  } catch (error) {

  }
  return null;
};

const getCacheConfig = (dataSource: DataSourceType, type: string) => {
  if (dataSource === 'contacts') {
    return { cache: null, duration: 0 };
  } else {
    return {
      cache: peopleRequestCache,
      duration: type === 'metadata' ? CACHE_DURATION * 5 : CACHE_DURATION
    };
  }
};

let lastAuthErrorTime = 0;
const AUTH_ERROR_DEBOUNCE_MS = 5000;

export const useApi = () => {
  const { dispatch, showToast } = useAppContext();
  const { apiKey } = useAuth();
  
  const handleAuthError = useCallback((error: AuthError, endpoint: string, backoffKey: string) => {
    const now = Date.now();
    // Enforce cooldown for every 429, even when the toast is debounced.
    const backoffTime = error.isRateLimit ? backoffManager.addFailure(backoffKey, true, error.retryMs ?? null) : 0;
    if (now - lastAuthErrorTime < AUTH_ERROR_DEBOUNCE_MS) {
      return;
    }
    lastAuthErrorTime = now;
    
    if (error.isAuthError) circuitBreaker.onFailure();
    
    if (error.isAuthError) {
      showToast({ type: 'error', title: 'Authentication Failed', message: 'Invalid API key. Redirecting to login...', duration: 3000 });
      dispatch({ type: 'SET_API_CONNECTED', payload: false });
      
      // Redirect to login page after short delay
      setTimeout(() => {
        localStorage.setItem('show_login_page', 'true');
        window.location.reload();
      }, 1000);
    } else if (error.isPermissionError) {
      showToast({ type: 'warning', title: 'Access Denied', message: `Insufficient permissions for ${endpoint}.`, duration: 6000 });
    } else if (error.isRateLimit) {
      showToast({ type: 'warning', title: 'Rate Limit Exceeded', message: `Too many requests. Waiting ${Math.round(backoffTime / 1000)}s.`, duration: 8080 });
    }
  }, [showToast, dispatch]);

  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [trackerState, setTrackerState] = useState<RequestTrackerState>(() => requestTracker.getState());
  const trackerStateRef = useRef(trackerState);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const handleStateChange = (newState: RequestTrackerState) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        trackerStateRef.current = newState;
        setTrackerState(newState);
      }, 50);
    };
    const unsubscribe = requestTracker.subscribe(handleStateChange);
    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  const makeRequest = useCallback(async <T>(
    url: string,
    options: RequestInit = {},
    cacheKey?: string,
    type?: RequestType,
    fields?: string[],
    dataSource: DataSourceType = 'people'
  ): Promise<{ data: T; requestId: string } | null> => {
    if (!circuitBreaker.canMakeRequest()) {
      throw new Error('Service temporarily unavailable (circuit breaker open)');
    }
    
    const backoffKey = `${apiKey}:${type || 'unknown'}-${url}`;
    const waitTime = backoffManager.shouldWait(backoffKey);
    if (waitTime > 0) {
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    
    const isOwnerField = fields?.includes('owner_id');
    const shouldSkipCache = isOwnerField && dataSource === 'contacts';
    
    if (cacheKey && type && !shouldSkipCache) {
      const { cache } = getCacheConfig(dataSource, type);
      if (cache && cache[cacheKey] && Date.now() < cache[cacheKey].expiry) {
        return { data: cache[cacheKey].data as T, requestId: 'cached' };
      }
    }

    const trackedRequest = type ? requestTracker.startRequest(type, fields) : null;
    const signal = trackedRequest?.abortController.signal;

    try {
      if (!apiKey || apiKey === 'null' || apiKey === 'undefined') return null;
      
      // Detect if body is FormData
      const isFormData = options.body instanceof FormData;
      const authHeaders = getAuthHeaders(apiKey, isFormData);
      const response = await fetch(url, { ...options, headers: { ...authHeaders, ...options.headers }, signal });

      if (signal?.aborted) return null;

      const newRateLimitInfo = parseRateLimitHeaders(response.headers);
      if (newRateLimitInfo) setRateLimitInfo(newRateLimitInfo);

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        let errorDetail = null;
        let errorCode: string | undefined;
        try {
          const errorData = await response.json();
          errorCode = errorData.code;
          if (errorData.detail) {
            errorMessage = errorData.detail;
            errorDetail = errorData.detail;
          }
        } catch {}

        if (response.status === 401 || response.status === 403 || response.status === 429) {
          const authError = createAuthError(response.status, errorMessage);
          authError.retryMs = retryAfterMs(response.headers.get('Retry-After'));
          handleAuthError(authError, url, backoffKey);
          throw authError;
        } else if (response.status === 402) {
          showToast({ type: 'warning', title: errorCode === 'search_preview_limit' ? 'Search Preview Limit' : 'Account Limit Reached', message: errorDetail || 'Payment required to continue.', duration: 8000 });
          const error = createAuthError(402, errorMessage);
          error.code = errorCode;
          throw error;
        } else if (response.status === 400 && url.includes('/api/search')) {
          const message = errorDetail && (errorDetail.includes('limited to the first') || errorDetail.includes('results'))
            ? errorDetail : errorDetail || errorMessage;
          const title = message === errorDetail ? 'Search Limit Reached' : 'Search Error';
          showToast({ type: 'warning', title, message, duration: 8000 });
          throw createAuthError(400, errorMessage);
        } else {
          backoffManager.addFailure(backoffKey, false, retryAfterMs(response.headers.get('Retry-After')));
          if (response.status >= 400 && response.status < 500) {
            // Handle validation errors (422) specially
            if (response.status === 422) {
              try {
                const errorData = await response.json();
                if (errorData.detail && Array.isArray(errorData.detail)) {
                  // Pydantic validation errors
                  const validationErrors = errorData.detail.map((err: any) => {
                    if (typeof err === 'object' && err.msg) {
                      return `${err.loc ? err.loc.join('.') : 'field'}: ${err.msg}`;
                    }
                    return String(err);
                  }).join('; ');
                  showToast({ type: 'error', title: 'Validation Error', message: validationErrors, duration: 8000 });
                  throw new Error(validationErrors);
                }
              } catch (parseError) {
                // Fallback to generic error handling
              }
            }
            showToast({ type: 'error', title: `Request Failed (${response.status})`, message: errorDetail || errorMessage, duration: 6000 });
          }
          throw new Error(errorMessage);
        }
      }

      const data = await readApiResponse<T>(response);

      if (signal?.aborted) return null;
      if (data === null || data === undefined) throw new Error('Empty response from server');

      circuitBreaker.onSuccess();
      backoffManager.clearFailures(backoffKey);

      if (!trackerState.isSearching) dispatch({ type: 'SET_API_CONNECTED', payload: true });

      if (cacheKey && !signal?.aborted && type && !shouldSkipCache) {
        const { cache, duration } = getCacheConfig(dataSource, type);
        if (cache && duration > 0) {
          cache[cacheKey] = { data, timestamp: Date.now(), expiry: Date.now() + duration };
        }
      }

      if (trackedRequest && !signal?.aborted) {
        requestTracker.completeRequest(trackedRequest.id, 'completed');
      }

      return { data, requestId: trackedRequest?.id || 'untracked' };
      
    } catch (error) {
      if (trackedRequest) {
        if (error instanceof Error && (error.message === 'Request was cancelled' || error.name === 'AbortError')) {
          return null;
        } else {
          requestTracker.completeRequest(trackedRequest.id, 'failed');
        }
      }
      if (error instanceof Error && error.name === 'AbortError') return null;
      const authError = error as AuthError;
      if (!authError.isAuthError && !authError.isPermissionError && !authError.isRateLimit) {
        console.error(`❌ Request failed for ${url}:`, error);
      }
      throw error;
    }
  }, [handleAuthError, dispatch, trackerState.isSearching, apiKey, showToast]);

  const checkApiHealth = useCallback(async (): Promise<boolean> => {
    if (!apiKey || apiKey === 'null' || apiKey === 'undefined') return false;
    try {
      const waitTime = backoffManager.shouldWait('health');
      if (waitTime > 0) await new Promise(resolve => setTimeout(resolve, waitTime));
      const result = await makeRequest(`${API_BASE_URL}/health`, {}, 'health', 'health');
      return !!result;
    } catch (error) {
      const authError = error as AuthError;
      if (authError.isPermissionError) {
        try {
          await new Promise(resolve => setTimeout(resolve, 1000));
          const rootResult = await makeRequest(`${API_BASE_URL}/`, {}, 'root_health', 'health');
          return !!rootResult;
        } catch {}
      }
      return false;
    }
  }, [makeRequest, apiKey]);

  const getSchema = useCallback(async (dataSource: DataSourceType = 'people'): Promise<SchemaResponse | null> => {
    try {
      if (rateLimitInfo && rateLimitInfo.remaining < 5) {
        const waitTime = Math.max(0, rateLimitInfo.reset - Date.now());
        if (waitTime > 0) await new Promise(resolve => setTimeout(resolve, waitTime));
      }
      const result = await makeRequest<SchemaResponse>(`${API_BASE_URL}/api/schema?data_source=${dataSource}`, {}, `schema_${dataSource}`, 'schema');
      if (!result) return null;
      const { data } = result;
      if (!data.success || !data.field_definitions || !data.sections || !data.column_configs) {
        throw new Error('Invalid schema response structure');
      }
      return data;
    } catch (error) {
      return null;
    }
  }, [makeRequest, rateLimitInfo]);

  const loadKeywordSuggestions = useCallback(async (dataSource: DataSourceType = 'people', requestType: RequestType = 'schema'): Promise<void> => {
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const result = await makeRequest<{ success: boolean; suggestions: Record<string, string[]>; }>(
        `${API_BASE_URL}/api/keyword_suggestions?data_source=${dataSource}`, {}, `keyword_suggestions_${dataSource}`, requestType, undefined, dataSource
      );
      if (result?.data.success && result.data.suggestions) {
        dispatch({ type: 'SET_KEYWORD_SUGGESTIONS', payload: result.data.suggestions });
      }
    } catch (error) {
      dispatch({ type: 'SET_KEYWORD_SUGGESTIONS', payload: {} });
    }
  }, [makeRequest, dispatch]);

  const getMetadata = useCallback(async (
    query?: string, filters?: Record<string, unknown>, metadataFields?: string[],
    dataSource: DataSourceType = 'people', requestType: string = 'metadata'
  ): Promise<string | null> => {
    try {
      dispatch({ type: 'SET_METADATA_LOADING', payload: true });
      const requestBody: Record<string, unknown> = { data_source: dataSource };
      const combinedFilters = { ...filters };
      if (query && query.trim()) {
        // This logic is now superseded by the corrected search logic
        // requestBody.query = query.trim(); // should it be this or no?
      }
      if (Object.keys(combinedFilters).length > 0) requestBody.filters = combinedFilters;
      requestBody.metadata_fields = metadataFields || [];
      const cacheKey = `metadata_${dataSource}_${JSON.stringify({ query, filters: requestBody.filters, fields: metadataFields })}`;
      
      const result = await makeRequest<MetadataResponse>(`${API_BASE_URL}/api/search_metadata`, { method: 'POST', body: JSON.stringify(requestBody) }, cacheKey, requestType as RequestType, metadataFields, dataSource);
      if (!result) return null;
      const { data, requestId } = result;
      if (!data.field_statistics || typeof data.field_statistics !== 'object') throw new Error('Invalid metadata response structure');
      dispatch({ type: 'SET_METADATA', payload: data });
      return requestId;
    } catch (error) {
      dispatch({ type: 'SET_METADATA_ERROR', payload: error instanceof Error ? error.message : 'Metadata request failed' });
      return null;
    } finally {
      if (!requestTracker.hasActiveRequestOfType('metadata')) {
        dispatch({ type: 'SET_METADATA_LOADING', payload: false });
      }
    }
  }, [makeRequest, dispatch]);

  // ✅ CRITICAL FIX: Correctly use the 'query' parameter for full-text search.
  const searchContacts = useCallback(async (searchParams: SearchState, pageSize: number, schema?: SchemaResponse): Promise<{ response: SearchResponse; requestId: string } | null> => {
    const sortingArray = Array.isArray(searchParams.sorting) ? searchParams.sorting : [];
    let actualSortField: string | undefined;
    let actualSortOrder: string | undefined;
    
    if (sortingArray.length > 0 && schema) {
      const columnId = sortingArray[0].id;
      const columnConfig = schema.column_configs?.find((col) => col.id === columnId);
      if (columnConfig) {
        actualSortField = columnConfig.sort_field;
        actualSortOrder = sortingArray[0].desc ? 'desc' : 'asc';
      }
    }

    const cleanFilters: Record<string, unknown> = {};
    if (searchParams.filters) {
      for (const [key, value] of Object.entries(searchParams.filters)) {
        if (value !== null && value !== undefined && !(Array.isArray(value) && value.length === 0) && !(typeof value === 'string' && value.trim() === '')) {
          cleanFilters[key] = value;
        }
      }
    }
    
    // ✅ CORRECTED LOGIC: Use 'query' parameter as intended by API docs.
    const requestBody: Record<string, unknown> = {
      query: searchParams.query && searchParams.query.trim() ? searchParams.query.trim() : undefined,
      page: searchParams.page,
      size: pageSize,
      filters: Object.keys(cleanFilters).length > 0 ? cleanFilters : undefined,
      data_source: searchParams.dataSource
    };

    // Add field grouping parameters if they exist
    if (cleanFilters.group_contact_keyword_fields !== undefined) {
      requestBody.group_contact_keyword_fields = cleanFilters.group_contact_keyword_fields;
      delete cleanFilters.group_contact_keyword_fields; // Remove from filters
    }
    if (cleanFilters.group_company_keyword_fields !== undefined) {
      requestBody.group_company_keyword_fields = cleanFilters.group_company_keyword_fields;
      delete cleanFilters.group_company_keyword_fields; // Remove from filters
    }

    if (actualSortField && actualSortOrder) {
      requestBody.sort_field = actualSortField;
      requestBody.sort_order = actualSortOrder;
    }

    try {
      const result = await makeRequest<SearchResponse>(`${API_BASE_URL}/api/search`, { method: 'POST', body: JSON.stringify(requestBody) }, undefined, 'search', undefined, searchParams.dataSource);
      if (!result) return null;
      const { data, requestId } = result;
      if (!data.hits || !Array.isArray(data.hits) || typeof data.total !== 'number') throw new Error('Invalid search response');
      return { response: data, requestId };
    } catch (error) {
      if (error instanceof Error && (error.message === 'Request cancelled' || error.name === 'AbortError')) return null;
      throw error;
    }
  }, [makeRequest]);

  const searchContactsBackground = useCallback(async (searchParams: SearchState, pageSize: number): Promise<{ response: SearchResponse; requestId: string } | null> => {
    const result = await makeRequest<SearchResponse>(`${API_BASE_URL}/api/search`, { method: 'POST', body: JSON.stringify({ query: searchParams.query || undefined, page: searchParams.page, size: pageSize, filters: Object.keys(searchParams.filters || {}).length > 0 ? searchParams.filters : undefined, data_source: searchParams.dataSource }) }, undefined, 'background_search', undefined, searchParams.dataSource);
    if (!result) return null;
    return { response: result.data, requestId: result.requestId };
  }, [makeRequest]);

  const loadDataSourceCount = useCallback(async (dataSource: DataSourceType): Promise<number | null> => {
    try {
      const searchParams = { query: '', page: 1, sorting: [], filters: {}, dataSource };
      const result = await searchContactsBackground(searchParams, 1);
      return result ? result.response.total : null;
    } catch (error) {
      return null;
    }
  }, [searchContactsBackground]);

  const loadInitialMetadata = useCallback(async (): Promise<void> => {
    try {
      const promises = [
        getMetadata(undefined, undefined, undefined, 'people', 'initial-metadata-people'),
        getMetadata(undefined, undefined, undefined, 'contacts', 'initial-metadata-contacts'),
        loadKeywordSuggestions('people', 'initial-suggestions-people'),
        loadKeywordSuggestions('contacts', 'initial-suggestions-contacts')
      ];
      await Promise.allSettled(promises);
    } catch (error) {}
  }, [getMetadata, loadKeywordSuggestions]);

  const getLists = useCallback(async (include_contact_counts: boolean = true) => {
    if (requestTracker.hasActiveRequestOfType('get_lists')) return null;
    try {
      const requestBody = { include_contact_counts };
      const result = await makeRequest(`${API_BASE_URL}/api/lists/get`, { method: 'POST', body: JSON.stringify(requestBody) }, undefined, 'get_lists');
      return result ? result.data : null;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const createList = useCallback(async (name: string, description?: string, tags?: string[], is_default?: boolean) => {
    try {
      const requestBody = { name, description, tags: tags || [], is_default: is_default || false };
      const result = await makeRequest(`${API_BASE_URL}/api/lists/create`, { method: 'POST', body: JSON.stringify(requestBody) }, undefined, 'create_list');
      return result ? result.data : null;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const deleteList = useCallback(async (list_id: string) => {
    try {
      const result = await makeRequest(`${API_BASE_URL}/api/lists/delete`, { method: 'POST', body: JSON.stringify({ list_id }) }, undefined, 'delete_list');
      return result?.data;
    } catch (error) { throw error; }
  }, [makeRequest]);

  // Account List Management
  const getAccountLists = useCallback(async (include_account_counts: boolean = true) => {
    if (requestTracker.hasActiveRequestOfType('get_account_lists')) return null;
    try {
      const requestBody = { include_account_counts };
      const result = await makeRequest(`${API_BASE_URL}/api/account-lists/get`, { method: 'POST', body: JSON.stringify(requestBody) }, undefined, 'get_account_lists');
      return result ? result.data : null;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const createAccountList = useCallback(async (name: string, description?: string) => {
    try {
      const requestBody = { name, description: description || '' };
      const result = await makeRequest(`${API_BASE_URL}/api/account-lists/create`, { method: 'POST', body: JSON.stringify(requestBody) }, undefined, 'create_account_list');
      return result ? result.data : null;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const deleteAccountList = useCallback(async (list_id: string) => {
    try {
      const result = await makeRequest(`${API_BASE_URL}/api/account-lists/${list_id}`, { method: 'DELETE' }, undefined, 'delete_account_list');
      return result?.data;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const addAccountsToList = useCallback(async (list_id: string, company_ids: string[]) => {
    try {
      const requestBody = { company_ids };
      const result = await makeRequest(`${API_BASE_URL}/api/account-lists/${list_id}/add-accounts`, { method: 'POST', body: JSON.stringify(requestBody) }, undefined, 'add_accounts_to_list');
      return result?.data;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const removeFromAccountListJob = useCallback(async (list_id: string, company_ids?: string[], select_all?: boolean, filters?: any) => {
    try {
      const requestBody: any = { list_id };
      if (company_ids) requestBody.company_ids = company_ids;
      if (select_all) requestBody.select_all = select_all;
      if (filters) requestBody.filters = filters;
      
      const result = await makeRequest(`${API_BASE_URL}/api/account-lists/remove/job`, { method: 'POST', body: JSON.stringify(requestBody) }, undefined, 'remove_from_account_list');
      return result?.data;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const deleteAccountsJob = useCallback(async (company_ids?: string[], select_all?: boolean, filters?: any) => {
    try {
      const requestBody: any = {};
      if (company_ids) requestBody.company_ids = company_ids;
      if (select_all) requestBody.select_all = select_all;
      if (filters) requestBody.filters = filters;
      
      const result = await makeRequest(`${API_BASE_URL}/api/accounts/delete/job`, { method: 'POST', body: JSON.stringify(requestBody) }, undefined, 'delete_accounts');
      return result?.data;
    } catch (error) { throw error; }
  }, [makeRequest]);

  // Account Unlock (FREE)
  const unlockAccounts = useCallback(async (company_ids: string[], list_ids: string[]) => {
    try {
      const requestBody = { company_ids, list_ids };
      const result = await makeRequest(`${API_BASE_URL}/api/accounts/unlock-jobs`, { method: 'POST', body: JSON.stringify(requestBody) }, undefined, 'unlock_accounts');
      return result?.data;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const searchAccounts = useCallback(async (query: string = '', page: number = 1, size: number = 20, list_id?: string) => {
    try {
      const params = new URLSearchParams({
        query,
        page: page.toString(),
        size: size.toString(),
        ...(list_id && { list_id })
      });
      const result = await makeRequest(`${API_BASE_URL}/api/accounts/search?${params}`, { method: 'GET' }, undefined, 'search_accounts');
      return result?.data;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const createUser = useCallback(async (name: string, permissions: string[], email?: string) => {
    try {
      const result = await makeRequest<User>(`${API_BASE_URL}/api/admin/users`, { method: 'POST', body: JSON.stringify({ name, permissions, email: email || undefined }) }, undefined, 'create_user');
      return result?.data;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const listUsers = useCallback(async (page: number = 1, size: number = 50, filterCustomPricing: boolean = false, search: string = ""): Promise<UsersResponse | null> => {
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const result = await makeRequest<UsersResponse>(`${API_BASE_URL}/api/admin/users/search?page=${page}&size=${size}&filter_custom_pricing=${filterCustomPricing}${searchParam}`, { method: 'POST' }, undefined, 'list_users');
      return result?.data || null;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const getUser = useCallback(async (userApiKey: string): Promise<User | null> => {
    try {
      const result = await makeRequest<User>(`${API_BASE_URL}/api/admin/users/${userApiKey}`, { method: 'GET' }, undefined, 'get_user');
      return result?.data || null;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const updateUser = useCallback(async (userApiKey: string, updates: { name?: string; permissions?: string[]; is_active?: boolean; rate_limit?: string | null; search_preview_limited?: boolean }) => {
    try {
      const result = await makeRequest(`${API_BASE_URL}/api/admin/users/${userApiKey}`, { method: 'PUT', body: JSON.stringify(updates) }, undefined, 'update_user');
      return result?.data;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const setCustomPricing = useCallback(async (userApiKey: string, customPrice: number | null, reason: string) => {
    try {
      const result = await makeRequest(`${API_BASE_URL}/api/admin/users/${userApiKey}/billing/pricing`, {
        method: 'PUT',
        body: JSON.stringify({
          custom_price_per_contact: customPrice,
          reason
        })
      }, undefined, 'set_custom_pricing');
      return result?.data;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const deleteUserAdmin = useCallback(async (userApiKey: string) => {
    try {
      const result = await makeRequest(`${API_BASE_URL}/api/admin/users/${userApiKey}`, { method: 'DELETE' }, undefined, 'delete_user');
      return result?.data;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const systemReset = useCallback(async (confirmationPhrase: string) => {
    try {
      const result = await makeRequest(`${API_BASE_URL}/api/admin/system/reset`, { method: 'POST', body: JSON.stringify({ confirm: confirmationPhrase }) }, undefined, 'system_reset');
      return result?.data;
    } catch (error) { throw error; }
  }, [makeRequest]);

  const getAnalyticsSummary = useCallback(async (hours: number = 24): Promise<AdminAnalyticsSummaryResponse | null> => {
    try {
      const result = await makeRequest<AdminAnalyticsSummaryResponse>(`${API_BASE_URL}/api/admin/analytics/summary?hours=${hours}`, { method: 'GET' }, `admin_analytics_summary_${hours}`, 'admin_analytics');
      return result?.data || null;
    } catch (error) {
      showToast({ type: 'error', title: 'Analytics Error', message: 'Could not load analytics summary data.' });
      return null;
    }
  }, [makeRequest, showToast]);

  const getExportConfig = useCallback(async (): Promise<ExportConfig | null> => {
    try {
      const result = await makeRequest<ExportConfig>(`${API_BASE_URL}/api/export/config`, { method: 'GET' }, 'export_config');
      return result?.data || null;
    } catch (error) {
      console.error('Failed to load export config:', error);
      return null;
    }
  }, [makeRequest]);

  return {
    searchContacts,
    searchContactsBackground,
    getMetadata,
    loadKeywordSuggestions,
    loadInitialMetadata,
    loadDataSourceCount,
    refreshMetadata: getMetadata,
    createList,
    getLists,
    deleteList,
    // Account List Management
    getAccountLists,
    createAccountList,
    deleteAccountList,
    addAccountsToList,
    removeFromAccountListJob,
    deleteAccountsJob,
    unlockAccounts,
    searchAccounts,
    createUser,
    listUsers,
    getUser,
    updateUser,
    setCustomPricing,
    deleteUserAdmin,
    systemReset,
    getAnalyticsSummary,
    requestTrackerState: trackerState,
    circuitBreakerState: circuitBreaker.getState(),
    rateLimitInfo,
    checkApiHealth,
    getSchema,
    getExportConfig,
    makeRequest
  };
};

export default useApi;
